import React from "react";
//#region types
import { StyledDateRange, StyledNumberRange, YearAndMonth, YearMonth } from "models/propTypes/commonPropTypes";
import { WintSuperkollAPIModelExternalCompanyData } from "wint-api-client/dist/types";
import {
  IconProfileInfo,
  FileProfileInfo,
  Customer,
  Supplier,
  IncomingInvoiceProfile,
  ReceiptProfile,
  PaymentMethodProfile,
  User,
  OrganizationInfo,
  CustomLink,
} from "models/propTypes/userProfilePropTypes";
import { Amount } from "models/utilTypes/currencyType";
import { Hours } from "models/utilTypes/hourType";
import { Percentage } from "models/utilTypes/percentageType";
import { TranslationKeys } from "translations";
import { DateConstants } from "utils/constants";
import { AmountPerUnit } from "models/utilTypes/amountPerUnitType";
import { UnitValue } from "models/utilTypes/unitValueType";
import { NumberFormat } from "xlsx";
//#endregion
//#region components
import * as Profiles from "../profile";
import Organization from "../profile/organization";
import DateRange from "../dateRange";
import NumberRange from "../numberRange";
import { Grid } from "@mui/material";
import { useCompanyService } from "serviceHooks";
//#endregion
import { DateHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

function useValueFormatter() {
  const companyService = useCompanyService();
  const getFormattedValue = (value: any) => {
    if (UtilsHelper.notUndefinedOrNull(value))
      switch (typeof value) {
        case "boolean":
          return value
            ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Yes)
            : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.No);
        case "number":
          return LocalizationHelperInstance.getFormattedNumber(value);
        case "object":
          if (value instanceof Date)
            return LocalizationHelperInstance.getFormattedDate(value, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            });
          else if (value instanceof OrganizationInfo) return <Organization {...value} />;
          else if (value instanceof Customer) return <Profiles.CustomerProfileLink {...value} />;
          else if (value instanceof Supplier) return <Profiles.SupplierProfileLink {...value} />;
          else if (value instanceof User) return <Profiles.UserProfileLink {...value} />;
          else if (value instanceof IconProfileInfo) return <Profiles.IconProfile {...value} />;
          else if (value instanceof FileProfileInfo) return <Profiles.FileProfile {...value} />;
          else if (value instanceof IncomingInvoiceProfile) return <Profiles.IncomingInvoiceProfileLink {...value} />;
          else if (value instanceof ReceiptProfile) return <Profiles.ReceiptProfileLink {...value} />;
          else if (value instanceof PaymentMethodProfile) return <Profiles.PaymentMethodProfileLink {...value} />;
          else if (value instanceof Amount)
            return value.amount !== undefined
              ? LocalizationHelperInstance.getFormattedCurrency(
                  value.amount,
                  value.currency,
                  0,
                  2,
                  undefined,
                  value.signDisplay
                )
              : "";
          else if (value instanceof StyledDateRange) return <DateRange {...value} />;
          else if (value instanceof StyledNumberRange) return <NumberRange {...value} />;
          else if (value instanceof YearMonth)
            return value.yearMonth
              ? LocalizationHelperInstance.getFormattedDate(
                  DateHelper.getDateFromYearMonth(value.yearMonth),
                  value.formatter ?? DateConstants.MonthYearNumericConfig
                )
              : "";
          else if (value instanceof YearAndMonth)
            return LocalizationHelperInstance.getFormattedDate(
              DateHelper.getDateFromAPIYearMonth(value),
              value.formatter ?? DateConstants.MonthYearNumericConfig
            );
          else if (value instanceof Hours) return value.hours !== undefined ? `${value.hours} ${value.hoursText}` : "";
          else if (value instanceof AmountPerUnit)
            return value.amount !== undefined
              ? `${LocalizationHelperInstance.getFormattedCurrency(value.amount, value.currency, 0, 2)}/${value.unit}`
              : "";
          else if (value instanceof CustomLink) return <Profiles.CustomLink {...value} />;
          else if (value instanceof Percentage)
            return value.value !== undefined
              ? `${LocalizationHelperInstance.getFormattedNumber(value.value * 100, 0, value.decimals)} %`
              : "";
          else if (value instanceof UnitValue) return value.value !== undefined ? `${value.value} ${value.unit}` : "";
          else return value;
        default:
          return value;
      }
    else return value;
  };

  const getFormattedValueForPrint = (value: any): string | (string | number)[] => {
    if (UtilsHelper.notUndefinedOrNull(value))
      switch (typeof value) {
        case "string":
          return value;
        case "boolean":
          return value
            ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Yes)
            : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.No);
        case "number":
          return LocalizationHelperInstance.getFormattedNumber(value);
        case "object":
          if (value instanceof Date)
            return LocalizationHelperInstance.getFormattedDate(value, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            });
          else if (value instanceof OrganizationInfo) return value.name ?? "";
          else if (value instanceof Customer) return value.name ?? "";
          else if (value instanceof Supplier) return value.name ?? "";
          else if (value instanceof User) return value.name ?? "";
          else if (value instanceof IconProfileInfo) return value.name;
          else if (value instanceof FileProfileInfo) return value.fileName;
          else if (value instanceof IncomingInvoiceProfile) return value.displayText;
          else if (value instanceof ReceiptProfile) return value.displayText;
          else if (value instanceof PaymentMethodProfile) return value.displayText;
          else if (value instanceof Amount) return [value.currency ?? "", value.amount as NumberFormat];
          else if (value instanceof StyledDateRange)
            return [
              value.start ? LocalizationHelperInstance.getFormattedDate(value.start) : "",
              value.end ? LocalizationHelperInstance.getFormattedDate(value.end) : "",
            ]
              .filter((value) => !value)
              .join(" - ");
          else if (value instanceof StyledNumberRange)
            return [
              value.lowest
                ? value.currencyCode
                  ? LocalizationHelperInstance.getFormattedCurrency(value.lowest, value.currencyCode)
                  : LocalizationHelperInstance.getFormattedNumber(value.lowest)
                : "",
              value.highest
                ? value.currencyCode
                  ? LocalizationHelperInstance.getFormattedCurrency(value.highest, value.currencyCode)
                  : LocalizationHelperInstance.getFormattedNumber(value.highest)
                : "",
            ]
              .filter((value) => !value)
              .join(" - ");
          else if (value instanceof YearMonth)
            return value.yearMonth
              ? LocalizationHelperInstance.getFormattedDate(
                  DateHelper.getDateFromYearMonth(value.yearMonth),
                  DateConstants.MonthYearNumericConfig
                )
              : "";
          else if (value instanceof YearAndMonth)
            return LocalizationHelperInstance.getFormattedDate(
              DateHelper.getDateFromAPIYearMonth(value),
              value.formatter ?? DateConstants.MonthYearNumericConfig
            );
          else if (value instanceof Hours) return value.hours !== undefined ? `${value.hours} ${value.hoursText}` : "";
          else if (value instanceof AmountPerUnit)
            return value.amount !== undefined
              ? `${LocalizationHelperInstance.getFormattedCurrency(value.amount, value.currency, 0, 2)}/${value.unit}`
              : "";
          else if (value instanceof CustomLink) return value.link;
          else if (value instanceof Percentage)
            return value.value !== undefined
              ? `${LocalizationHelperInstance.getFormattedNumber(value.value * 100, 0, value.decimals)} %`
              : "";
          else if (value instanceof UnitValue) return value.value !== undefined ? `${value.value} ${value.unit}` : "";
          else return "";
        default:
          return "";
      }
    else return "";
  };

  const instanceOfCurrencyValue = (object: any) => {
    return "type" in object && "value" in object;
  };

  const getSumamryFormattedValue = (value: any) => {
    if (UtilsHelper.notUndefinedOrNull(value)) {
      if (value instanceof Amount && typeof value === "object") {
        return value.amount !== undefined
          ? LocalizationHelperInstance.getFormattedCurrency(value.amount, value.currency, 0, 2)
          : "";
      }
    }
    return value;
  };

  const processArrayToPrint = (data: any[]) => {
    let cellValue = ``;

    data.forEach((value, index) => {
      cellValue = cellValue.concat(index > 0 ? ` , ` : ``);

      switch (typeof value) {
        case "number":
          cellValue = cellValue.concat(LocalizationHelperInstance.getFormattedNumber(value));
          break;
        case "object": {
          if (value instanceof Amount) {
            cellValue = cellValue.concat(
              value.amount !== undefined
                ? LocalizationHelperInstance.getFormattedCurrency(value.amount, value.currency, 0, 2)
                : ``
            );
          } else if (instanceOfCurrencyValue(value))
            cellValue = cellValue.concat(
              value.value !== undefined
                ? LocalizationHelperInstance.getFormattedCurrency(value.value, value.type, 0, 2)
                : ``
            );
          break;
        }
        case "string":
          cellValue = cellValue.concat(value);
          break;
        default:
          break;
      }
    });

    return cellValue;
  };

  const getSumamryFormattedValueForPrint = (value: any) => {
    if (UtilsHelper.notUndefinedOrNull(value)) {
      switch (typeof value) {
        case "string":
          return value;
        case "number":
          return LocalizationHelperInstance.getFormattedNumber(value);
        case "object": {
          if (value instanceof Amount) {
            return value.amount !== undefined
              ? LocalizationHelperInstance.getFormattedCurrency(value.amount, value.currency, 0, 2)
              : "";
          } else if (instanceOfCurrencyValue(value)) {
            return value.value !== undefined
              ? LocalizationHelperInstance.getFormattedCurrency(value.value, value.type, 0, 2)
              : "";
          } else if (value instanceof Array) {
            return processArrayToPrint(value);
          } else {
            return "";
          }
        }
        default:
          return "";
      }
    } else return "";
  };

  const getSummaryFromExternalVatInfo = (value?: WintSuperkollAPIModelExternalCompanyData.ExternalVatInfo) => {
    if (value === undefined) return "";

    let row1 = `${getFormattedValue(value.vat)}`;
    row1 = row1.concat(value.vat ? ` - ${companyService.getTextByVatType(value.period)}` : "");

    const row2 =
      value.vat && value.paymentDate
        ? `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Paid)} - ${DateHelper.formatDate(
            value.paymentDate
          )}`
        : undefined;

    return (
      <Grid item container>
        <>{row1}</>
        {row2 && (
          <>
            <br />
            <>{row2}</>
          </>
        )}
      </Grid>
    );
  };

  return {
    getFormattedValue,
    getFormattedValueForPrint,
    getSumamryFormattedValue,
    getSumamryFormattedValueForPrint,
    getSummaryFromExternalVatInfo,
  };
}

export default useValueFormatter;
